<template>
  <div v-loading="loading" class="nx-page meterenergy-usage-dashboard">
    <section class="filter-panel">
      <div class="filter-left">
        <el-date-picker
          v-model="billingMonth"
          type="month"
          value-format="yyyyMM"
          clearable
          size="small"
          placeholder="选择月份"
        />
        <el-select value="waterElectricCost" size="small" disabled>
          <el-option label="水电成本" value="waterElectricCost" />
        </el-select>
      </div>
      <div class="filter-actions">
        <el-button size="small" @click="reset">重置</el-button>
        <el-button size="small" type="primary" icon="el-icon-search" @click="load">查询</el-button>
      </div>
    </section>

    <section class="metrics">
      <el-card v-for="item in metricCards" :key="item.label" class="metric-card" shadow="never">
        <div class="metric-head">
          <span>{{ item.label }}</span>
          <span :class="['metric-icon', item.color]">{{ item.icon }}</span>
        </div>
        <div class="metric-value">{{ item.value }}<span>{{ item.unit }}</span></div>
        <div class="metric-foot">{{ item.desc }}</div>
      </el-card>
    </section>

    <section class="main-grid">
      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <span class="panel-title">园区成本主仪表盘</span>
          <el-tag size="mini" type="primary">水电总成本</el-tag>
        </div>
        <div class="cost-overview">
          <div ref="costGauge" class="cost-gauge" />
          <div class="cost-lines">
            <div v-for="item in costBars" :key="item.label" class="cost-row">
              <span class="cost-name">{{ item.label }}</span>
              <div class="bar-track"><span :class="['bar-fill', item.color]" :style="{ width: item.percent + '%' }"></span></div>
              <strong>{{ formatMoney(item.amount) }}</strong>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="panel-card" shadow="never">
        <div slot="header" class="panel-head">
          <div>
            <span class="panel-title">{{ costPieTitle }}</span>
            <div class="crumbs">{{ costPieCrumbs }}</div>
          </div>
          <el-button v-if="costPieMode !== 'overview'" size="mini" type="text" @click="backCostOverview">返回总览</el-button>
        </div>
        <div class="pie-layout">
          <div ref="costPie" class="pie-chart" />
          <div class="legend-list">
            <div
              v-for="item in currentCostLegend"
              :key="item.name"
              :class="['legend-item', { 'is-clickable': costPieMode === 'overview' }]"
              @click="handleLegendClick(item)"
            >
              <div class="legend-main">
                <span><i :class="['swatch', item.color]"></i>{{ item.name }}</span>
                <strong>{{ formatPercent(item.percent) }}%</strong>
              </div>
              <div class="legend-sub">
                <span>{{ formatMoney(item.amount) }} 元</span>
                <span v-if="costPieMode === 'overview'">点击查看生产/生活</span>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </section>

    <section class="usage-grid">
      <el-card class="usage-card" shadow="never">
        <div class="usage-title">全厂用量环比指数（100=持平）</div>
        <div ref="g1" class="usage-gauge" />
      </el-card>
      <el-card class="usage-card" shadow="never">
        <div class="usage-title">电占电+水用量比例</div>
        <div ref="g2" class="usage-gauge" />
      </el-card>
      <el-card class="usage-card" shadow="never">
        <div class="usage-title">水/电抄表条数比</div>
        <div ref="g3" class="usage-gauge" />
      </el-card>
    </section>

    <el-card class="panel-card trend-card" shadow="never">
      <div slot="header" class="panel-head">
        <span class="panel-title">近 6 月抄表记录条数</span>
        <el-tag size="mini">原有趋势</el-tag>
      </div>
      <div ref="chartBar" class="trend-chart" />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchUsageDashboard } from './api'

export default {
  name: 'MeterEnergyUsageDashboard',
  data() {
    return {
      loading: false,
      billingMonth: null,
      data: {},
      charts: [],
      costPieChart: null,
      costPieMode: 'overview'
    }
  },
  computed: {
    displayBillingMonth() {
      const bm = this.data.billingMonth || this.billingMonth
      if (!bm) return '--'
      return String(bm).replace(/^(\d{4})(\d{2})$/, '$1-$2')
    },
    metricCards() {
      return [
        {
          label: '当日累计总成本',
          value: this.formatMoney(this.data.todayTotalCost),
          unit: '元',
          icon: '日',
          color: 'blue',
          desc: '账期有效记录按日分摊'
        },
        {
          label: '当月累计总成本',
          value: this.formatMoney(this.data.monthTotalCost),
          unit: '元',
          icon: '月',
          color: 'green',
          desc: '水、电本期费用合计'
        },
        {
          label: '人均能耗成本',
          value: this.formatMoney(this.data.perCapitaCost),
          unit: '元/人',
          icon: '人',
          color: 'amber',
          desc: `园区总人数 ${this.formatInteger(this.data.userCount)} 人`
        },
        {
          label: '水电成本构成',
          value: `${this.formatPercent(this.electricSharePercent)}`,
          unit: '% 电',
          icon: '占',
          color: 'cyan',
          desc: `水占比 ${this.formatPercent(this.waterSharePercent)}%`
        }
      ]
    },
    electricCost() {
      return this.costAmountByFeeType(1)
    },
    waterCost() {
      return this.costAmountByFeeType(2)
    },
    electricSharePercent() {
      return this.percentByFeeType(1)
    },
    waterSharePercent() {
      return this.percentByFeeType(2)
    },
    costBars() {
      const breakdown = this.data.costBreakdown || {}
      const electricGroup = this.groupSum(breakdown.electric)
      const waterGroup = this.groupSum(breakdown.water)
      const prod = electricGroup.production + waterGroup.production
      const life = electricGroup.life + waterGroup.life
      const groupTotal = prod + life
      return [
        { label: '电成本', amount: this.electricCost, percent: this.safePercent(this.electricCost, this.data.monthTotalCost), color: 'electric' },
        { label: '水成本', amount: this.waterCost, percent: this.safePercent(this.waterCost, this.data.monthTotalCost), color: 'water' },
        { label: '生产', amount: prod, percent: this.safePercent(prod, groupTotal), color: 'prod' },
        { label: '生活', amount: life, percent: this.safePercent(life, groupTotal), color: 'life' }
      ]
    },
    costPieTitle() {
      if (this.costPieMode === 'electric') return '电成本生产/生活'
      if (this.costPieMode === 'water') return '水成本生产/生活'
      return '水电成本占比'
    },
    costPieCrumbs() {
      if (this.costPieMode === 'electric') return '总览 / 电成本'
      if (this.costPieMode === 'water') return '总览 / 水成本'
      return '总览'
    },
    currentCostLegend() {
      if (this.costPieMode === 'electric') return this.groupLegend('electric')
      if (this.costPieMode === 'water') return this.groupLegend('water')
      return [
        { name: '电成本', amount: this.electricCost, percent: this.electricSharePercent, color: 'electric', drill: 'electric' },
        { name: '水成本', amount: this.waterCost, percent: this.waterSharePercent, color: 'water', drill: 'water' }
      ]
    }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    this.disposeCharts()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await fetchUsageDashboard({ billingMonth: this.billingMonth || undefined })
        this.data = (res.data && res.data.data) || {}
        this.costPieMode = 'overview'
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    reset() {
      this.billingMonth = null
      this.load()
    },
    resize() {
      this.charts.forEach((c) => c.resize())
    },
    disposeCharts() {
      this.charts.forEach((c) => c.dispose())
      this.charts = []
      this.costPieChart = null
    },
    renderCharts() {
      this.disposeCharts()
      this.renderCostGauge()
      this.renderCostPie()
      this.renderUsageGauges()
      this.renderTrend()
    },
    renderCostGauge() {
      const chart = echarts.init(this.$refs.costGauge)
      chart.setOption({
        series: [
          {
            type: 'gauge',
            min: 0,
            max: Math.max(Number(this.data.monthTotalCost || 0), 1),
            startAngle: 210,
            endAngle: -30,
            axisLine: { lineStyle: { width: 16, color: [[0.65, '#2563eb'], [0.82, '#14b8a6'], [1, '#e8edf5']] } },
            pointer: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            detail: {
              valueAnimation: true,
              formatter: () => `${this.formatMoney(this.data.monthTotalCost)}\n元/月`,
              fontSize: 22,
              lineHeight: 30,
              color: '#1f2937'
            },
            data: [{ value: Number(this.data.monthTotalCost || 0) }]
          }
        ]
      })
      this.charts.push(chart)
    },
    renderCostPie() {
      const chart = this.costPieChart || echarts.init(this.$refs.costPie)
      this.costPieChart = chart
      const rows = this.currentCostLegend.map((item) => ({
        name: item.name,
        value: Number(item.amount || 0),
        drill: item.drill,
        itemStyle: { color: this.legendColor(item.color) }
      }))
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}<br/>{c} 元 ({d}%)' },
        series: [
          {
            type: 'pie',
            radius: ['45%', '72%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,
            label: { formatter: '{b}\n{d}%', color: '#374151' },
            data: rows
          }
        ]
      }, true)
      chart.off('click')
      chart.on('click', (params) => {
        if (this.costPieMode === 'overview' && params.data && params.data.drill) {
          this.costPieMode = params.data.drill
          this.$nextTick(() => this.renderCostPie())
        }
      })
      if (this.charts.indexOf(chart) === -1) {
        this.charts.push(chart)
      }
    },
    renderUsageGauges() {
      const d = this.data
      this.renderGauge('g1', 70, 130, Math.min(130, Math.max(0, Number(d.compositeMomIndex) || 100)), '指数', '{value}')
      this.renderGauge('g2', 0, 100, Math.min(100, Math.max(0, Number(d.electricShareOfElectricPlusWaterPercent) || 0)), '%', '{value}%')
      this.renderGauge('g3', 0, 2, Math.min(2, Math.max(0, Number(d.waterToElectricRecordCountRatio) || 0)), '水/电', '{value}:1')
    },
    renderGauge(ref, min, max, value, name, formatter) {
      const chart = echarts.init(this.$refs[ref])
      chart.setOption({
        series: [
          {
            type: 'gauge',
            min,
            max,
            startAngle: 200,
            endAngle: -20,
            splitNumber: 4,
            axisLabel: { color: '#64748b', fontSize: 10 },
            axisLine: { lineStyle: { width: 12, color: [[0.35, '#16a34a'], [0.7, '#d97706'], [1, '#2563eb']] } },
            pointer: { itemStyle: { color: '#475569' } },
            detail: { formatter, fontSize: 16, color: '#0f172a' },
            data: [{ value: Number(Number(value).toFixed(2)), name }]
          }
        ]
      })
      this.charts.push(chart)
    },
    renderTrend() {
      const trend = this.data.recordCountTrend || []
      const chart = echarts.init(this.$refs.chartBar)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 48, right: 16, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: trend.map((x) => x.billingMonth), axisLabel: { color: '#64748b' } },
        yAxis: {
          type: 'value',
          name: '条',
          nameTextStyle: { color: '#64748b' },
          axisLabel: { color: '#64748b' },
          splitLine: { lineStyle: { color: '#e2e8f0' } }
        },
        series: [{ type: 'bar', data: trend.map((x) => x.recordCount || 0), itemStyle: { color: '#2563eb' } }]
      })
      this.charts.push(chart)
    },
    handleLegendClick(item) {
      if (this.costPieMode !== 'overview' || !item.drill) return
      this.costPieMode = item.drill
      this.$nextTick(() => this.renderCostPie())
    },
    backCostOverview() {
      this.costPieMode = 'overview'
      this.$nextTick(() => this.renderCostPie())
    },
    costAmountByFeeType(feeType) {
      const rows = this.data.costSharePie || []
      const found = rows.find((item) => Number(item.feeType) === Number(feeType))
      if (found) return Number(found.costAmount || 0)
      return Number(feeType) === 1 ? Number(this.data.electricCostAmount || 0) : Number(this.data.waterCostAmount || 0)
    },
    percentByFeeType(feeType) {
      const rows = this.data.costSharePie || []
      const found = rows.find((item) => Number(item.feeType) === Number(feeType))
      return found ? Number(found.percent || 0) : this.safePercent(this.costAmountByFeeType(feeType), this.data.monthTotalCost)
    },
    groupLegend(key) {
      const rows = ((this.data.costBreakdown || {})[key] || [])
      return rows.map((item) => {
        const isLife = item.groupName === '生活'
        return {
          name: item.groupName || '-',
          amount: Number(item.costAmount || 0),
          percent: Number(item.percent || 0),
          color: isLife ? 'life' : 'prod'
        }
      })
    },
    groupSum(rows = []) {
      return rows.reduce((result, item) => {
        if (item.groupName === '生活') {
          result.life += Number(item.costAmount || 0)
        } else {
          result.production += Number(item.costAmount || 0)
        }
        return result
      }, { production: 0, life: 0 })
    },
    safePercent(value, total) {
      const t = Number(total || 0)
      if (!t) return 0
      return Number(((Number(value || 0) / t) * 100).toFixed(2))
    },
    legendColor(name) {
      const map = {
        electric: '#f59e0b',
        water: '#0ea5e9',
        prod: '#16a34a',
        life: '#0891b2'
      }
      return map[name] || '#2563eb'
    },
    formatMoney(value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatPercent(value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    }
  }
}
</script>

<style scoped lang="scss">
.meterenergy-usage-dashboard {
  min-height: 100%;
  padding: 18px 20px 24px;
  background: #f5f7fb;
  color: #1f2937;
}

.topbar,
.filter-panel,
.panel-card,
.metric-card,
.usage-card {
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 18px;
  background: #fff;
}

.page-title {
  font-size: 18px;
  font-weight: 700;
}

.subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.top-actions,
.filter-left,
.filter-actions,
.panel-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  color: #0f766e;
  background: #ccfbf1;
  border-radius: 999px;
  font-size: 12px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #14b8a6;
}

.filter-panel {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 14px 16px;
  background: #fff;
}

.filter-left {
  flex: 1;
  flex-wrap: wrap;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.metric-card {
  min-height: 118px;
}

.metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #6b7280;
  font-size: 13px;
}

.metric-icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}

.metric-icon.blue { background: #2563eb; }
.metric-icon.green { background: #16a34a; }
.metric-icon.amber { background: #d97706; }
.metric-icon.cyan { background: #0891b2; }

.metric-value {
  margin-top: 12px;
  overflow: hidden;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    margin-left: 4px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
  }
}

.metric-foot {
  margin-top: 9px;
  color: #6b7280;
  font-size: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: minmax(420px, 1.05fr) minmax(360px, .95fr);
  gap: 14px;
  margin-top: 14px;
}

.panel-card {
  ::v-deep .el-card__header {
    padding: 0;
  }
}

.panel-head {
  justify-content: space-between;
  min-height: 52px;
  padding: 12px 16px;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
}

.crumbs {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.cost-overview {
  display: grid;
  grid-template-columns: 230px 1fr;
  gap: 20px;
  align-items: center;
  min-height: 318px;
  padding: 16px;
}

.cost-gauge {
  width: 100%;
  height: 240px;
}

.cost-lines {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cost-row {
  display: grid;
  grid-template-columns: 86px 1fr 116px;
  gap: 12px;
  align-items: center;
}

.cost-name {
  color: #6b7280;
  font-size: 13px;
}

.bar-track {
  height: 12px;
  overflow: hidden;
  background: #eef2f7;
  border-radius: 999px;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.bar-fill.electric { background: #f59e0b; }
.bar-fill.water { background: #0ea5e9; }
.bar-fill.prod { background: #16a34a; }
.bar-fill.life { background: #0891b2; }

.cost-row strong {
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pie-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 18px;
  align-items: center;
  min-height: 318px;
  padding: 16px;
}

.pie-chart {
  width: 100%;
  height: 260px;
}

.legend-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  padding: 12px;
  background: #fbfcfe;
  border: 1px solid #e5e8ef;
  border-radius: 8px;
}

.legend-item.is-clickable {
  cursor: pointer;
}

.legend-main,
.legend-sub {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.legend-main {
  font-weight: 700;
}

.legend-sub {
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
}

.swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 2px;
}

.swatch.electric { background: #f59e0b; }
.swatch.water { background: #0ea5e9; }
.swatch.prod { background: #16a34a; }
.swatch.life { background: #0891b2; }

.usage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.usage-card {
  background: #fff;
}

.usage-title {
  margin-top: 12px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

.usage-gauge {
  width: 100%;
  height: 190px;
}

.trend-card {
  margin-top: 14px;
}

.trend-chart {
  width: 100%;
  height: 260px;
}

@media (max-width: 1360px) {
  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .main-grid,
  .cost-overview,
  .pie-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .meterenergy-usage-dashboard {
    padding: 12px;
  }

  .topbar,
  .filter-panel {
    align-items: stretch;
    flex-direction: column;
  }

  .top-actions,
  .filter-actions {
    justify-content: flex-start;
  }

  .metrics,
  .usage-grid {
    grid-template-columns: 1fr;
  }

  .main-grid {
    grid-template-columns: 1fr;
  }

  .cost-row {
    grid-template-columns: 70px 1fr;
  }

  .cost-row strong {
    grid-column: 2;
    text-align: left;
  }
}
</style>
